<template>
  <v-card v-if="matter && currContact"
          class="matter-pane-wrapper"
          :class="{ 'matter-pane-desktop': !isMobile }">

    <v-card class="matter-pane-desktop-header"
            v-if="!isMobile && !inboxReadingPaneFF"
            :color="primaryColor"
            @click="matterHeaderClick">

      <div class="matter-pane-desktop-header__title">
        {{$t(matterTerm)}}
      </div>

      <v-tooltip top
                 content-class="click-tooltip">
        <template v-slot:activator="{ on, attrs }">
          <div class="matter-pane-desktop-header__name"
               v-bind="attrs"
               v-on="on">
            {{matter.display_name}}
          </div>
        </template>
        <span>{{$t('client.matters.matter_page.click_to_expand')}}</span>
      </v-tooltip>

    </v-card>

    <v-tooltip bottom content-class="click-tooltip" z-index="100">
      <template v-slot:activator="{ on, attrs }">
        <v-card class="matter-pane-desktop-header expand"
                v-if="!isMobile && inboxReadingPaneFF" v-bind="attrs" v-on="on"
                :color="primaryColor"
                @click="matterHeaderClick">
          <div class="matter-pane-desktop-header__description">
            <div class="matter-pane-desktop-header__title">
              {{$t(matterTerm)}}
            </div>

            <div class="matter-pane-desktop-header__name">
              {{matter.display_name}}
            </div>
          </div>
          <VcIcon color="white" size="18">icon-Expand</VcIcon>
        </v-card>
      </template>
      <span>{{$t('client.matters.matter_page.click_to_expand')}}</span>
    </v-tooltip>

    <div class="matter-pane-body" :class="{'mobile-view': isMobile}">
      <GeneralTabs :tabs="tabs"
                   touchless
                   :active-tab="activeTabIndex"
                   :slider-color="tabsColor"
                   :on-hover-color="tabsColor"
                   :disable-hover="isMobile"
                   @tab-clicked="onTabChange"
                   :tabTooltip="!isMobile"
                   :background-color="primaryColor">

        <template v-slot:tabTitle="{tab, index, activeTabIndex}"
                  v-if="!isMobile">

          <template v-if="tab.customCmp">
            <AvatarImage class="contact-avatar"
                         v-if="currContact"
                         size="sm"
                         :image-path="currContact.photo_path"
                         :name="currContact.full_name"
                         :color-id="currContact.color_id"/>
            <StatusIcon class="contact-status-icon"
                        v-if="currContact"
                        :status="contactStatus.value"
                        inverted/>
          </template>

          <v-icon v-else
                  class="tab-header-icon"
                  :color="activeTabIndex === index ? tabsColor : 'inherit'">
            {{tab.icon}}
          </v-icon>

        </template>

        <v-tab-item v-for="(tab, idx) in tabs"
                    :value="idx"
                    :key="idx">
          <component :is="tab.cmp"
                     v-if="tab.id === tabs[activeTabIndex].id"
                     :matter="matter"
                     :contact="currContact"
                     :isPane="true"
                     :isSmallScreen="true"
                     :isMobile="isMobile"
                     :direction="direction"
                     :showFab="true"/>
        </v-tab-item>
      </GeneralTabs>
    </div>
  </v-card>
  <IconsLoader v-else size="sm"
    class="app-loader"/>

</template>

<script>
  import {GeneralTabs, AvatarImage, IconsLoader, StatusIcon} from "@/modules/common/components";
  import {Conversation, Notes, Bookings, Payments, Documents, Docuforms, MatterInfo, ContactInfo} from '@/modules/matters/components';
  import {mapGetters} from "vuex";
  import tabsService from "@/modules/matters/services/tabsService";
  import Nesting from "@/modules/matters/components/Nesting/Nesting";

  export default {
    name: "MatterPane",
    components: {
      GeneralTabs,
      IconsLoader,
      Conversation,
      Notes,
      Bookings,
      Payments,
      Documents,
      Docuforms,
      MatterInfo,
      ContactInfo,
      AvatarImage,
      StatusIcon,
      Nesting
    },
    props: {
      activeTab: {
        type: [Number, String],
        required: true
      }
    },
    async created(){
      this.$frontageApi.listen('show_contact_tab', this.showContactTab);
      this.$frontageApi.listen('show_conversation_tab', this.showConversationTab);
      this.$root.$on('docuform-matter-pane-conversation', this.showConversationTab);
      this.setActiveTabIndex();
    },
    data(){
      return {
        activeTabIndex: 0
      }
    },
    watch: {
      activeTab: function () {
        this.setActiveTabIndex()
      }
    },
    computed: {
      ...mapGetters(['isMobile', 'direction', 'angularOrigin']),
      ...mapGetters('mattersModule', ['matter', 'currContact']),
      ...mapGetters("brandModule", ["primaryColor"]),
      inboxReadingPaneFF() {
        return this.$hasFeature('inbox_reading_pane');
      },
      tabsColor() {
        return this.$store.getters['brandModule/hoverColor'];
      },
      matterTerm(){
        return this.$store.getters['accountModule/businessTerm'].capitalized_singular || 'settings.business_page.business_terminology.terms.clients.capitalized_singular';
      },
      tabs() {
        return tabsService.getTabs(
          'info',
          'conversation',
          'notes',
          'bookings',
          this.$hasPermission('can_view_payments') && this.$hasFeature('payments_module') && 'payments',
          this.$hasPermission('can_view_documents') && !this.$hasFeature('new_documents') && 'documents',
          this.$hasPermission('can_view_documents') && this.$hasFeature('new_documents') && 'docuforms',
          'contact');
      },
      contactStatus() {
        return this.currContact.data.find(field => field.field_type === 'status');
      }
    },
    methods: {
      onTabChange(newTab) {
        this.setActiveTabIndex(this.tabs.findIndex((tab) => tab.id === newTab.id));
        sessionStorage.setItem('activeMatterTabMobile', this.activeTabIndex.toString());
        this.$track(`Client card ${newTab.id} tab`);
      },
      matterHeaderClick() {
        this.$track('Expand client card', {origin: this.angularOrigin})
        this.currContact && this.$frontageApi.navigateTo('app.client', {uid: this.currContact.uid, matter_uid: this.matter.uid});
      },
      showContactTab() {
        const newTab = this.tabs.find((tab) => tab.id === 'contact');
        this.onTabChange(newTab);
      },
      showConversationTab() {
        const newTab = this.tabs.find((tab) => tab.id === 'conversation');
        this.onTabChange(newTab);
      },
      setActiveTabIndex(tabIndex) {
        if(Number.isInteger(tabIndex)) {
          this.activeTabIndex = tabIndex;
        } else if(typeof this.activeTab === 'string') {
          this.activeTabIndex = this.tabs.findIndex(tab => tab.id === this.activeTab);
        } else {
          this.activeTabIndex = this.activeTab;
        }
      }
    },
    destroyed() {
      this.$frontageApi.remove('show_contact_tab', this.showContactTab);
      this.$frontageApi.remove('show_conversation_tab', this.showConversationTab);
      this.$root.$off('docuform-matter-pane-conversation', this.showConversationTab);
    }
  }
</script>

<style scoped lang="scss">
  @import "@/assets/scss/abstract/_variables.scss";
  @import "@/assets/scss/abstract/_mixins.scss";

  .matter-pane-wrapper {
    font-family: var(--primary-font-family);
    overflow: hidden;
    height: 100vh;
    border-radius: 0 !important;
    display: flex;
    flex-direction: column;

    .v-tabs {
      background-color: $grey-color-3;

      ::v-deep .v-slide-group__wrapper {
        border-radius: 20px 20px 0 0;
        background-color: $white-color-1;
        border-bottom: solid 1px $grey-color-2;
      }

      ::v-deep .v-slide-group__content {
        padding: 0 5px;
      }

      ::v-deep .v-tab {
        text-transform: none;
        color: $grey-color-4 !important;
        font-weight: 600;
        font-size: 15px;
        letter-spacing: -0.32px;
        padding: 0;
        min-width: auto;
        margin: 0 15px;
        opacity: 1 !important;

        &--active {
          color: $color_dark_text !important;

          &::before {
            opacity: 0 !important;
          }
        }

        &:not(.v-tab--active) {

          &:hover {

            &::before {
              opacity: 0;
            }
          }
        }
      }

      ::v-deep .v-item-group {
        max-width: 1200px;
        border-radius: 0;
      }

      ::v-deep .v-tabs-bar {
        position: sticky;
        z-index: 99;
        width: 100%;
        top: 0;
      }

      ::v-deep .v-window {
        width: 100%;
      }
    }

    .matter-pane-desktop-header {
      position: sticky;
      z-index: 100;
      top: 0;

      &.expand {
        display: flex;
        justify-content: space-between;
      }
    }

    .matter-pane-body {
      width: 100%;
      height: 100%;
      overflow: scroll;
      scrollbar-width: none;
      &::-webkit-scrollbar {
        display: none;
      }

      &.mobile-view {
        top: 0;
      }
    }

    ::v-deep .v-expansion-panel::before {
      box-shadow: none;
    }

    ::v-deep .v-expansion-panel-header {

      &:hover {
        color: $color_dark_text;
      }
    }

    .matter-engagement-tab {
      box-shadow: none;
      width: 100vw;
      overflow: auto;
      background-color: $white-color-1;
    }

    ::v-deep .empty-state {
      color: $grey-color-6;
      padding: 28px 0;

      &.empty-matter-fields {
        padding: 15px 0;
      }

      &.non-empty {
        padding: 5px 0;
      }

      &-content {
        position: relative;
        inset-inline-start: 50%;
        @include i-translate-x(-50%);
        width: fit-content;
      }

      &-title {
        font-weight: 600;
      }

      &-cta {
        padding-top: 5px;
        color: $blue-color-4;
        font-weight: 500;
        cursor: pointer;

        .v-icon {
          color: $blue-color-4;
          font-size: 16px;
          top: -2px;
        }
      }
    }

    .entity-list {
      height: 100%;
    }

    ::v-deep .credit-card {

      .default-card {
        inset-inline-end: -80%;
      }
    }

    .matter-info {
      padding-bottom: 50px;
    }
  }

  .matter-pane-desktop {

    &-header {
      height: 65px;
      color: $white-color-1;
      padding: 10px 20px;

      .v-card & {
        border-radius: 0;
      }

      &__title {
        font-size: 12px;
        opacity: 0.59;
      }

      &__name {
        font-size: 15px;
        font-weight: 700;
        width: fit-content;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        max-width: 100%;
      }
    }

    ::v-deep .v-tabs {

      .v-item-group {
        border: none;
      }

      .v-slide-group__wrapper {
        border-radius: 0;
      }

      .v-slide-group__content {
        padding: 0;

        .v-icon {
          font-size: 30px;

          &.status-icon {
            color: $white-color-1 !important;
          }
        }

        .v-tab {
          margin: 0;
          width: 100%;

          &--active {

            .status-icon {
              color: $white-color-1 !important;
            }
          }
        }
      }
    }

    .v-window-item {
      width: 370px;

      .matter-info, .contact-info {
        width: 100%;

        ::v-deep .contact-box {
          border-radius: 0;

          .v-expansion-panels {
            padding: 0;
          }

          .v-expansion-panel {
            box-shadow: none;
          }
        }
      }
    }
  }

  .contact-avatar {
    width: 35px;
    height: 35px;
  }

  .tab-header-icon {
    transition: none;
  }

  .contact-status-icon.status-icon {
    font-size: 9px !important;
    height: 15px;
    width: 15px;
    position: absolute;
    top: 33px;
    left: 29px;
  }

  .click-tooltip {
    font-size: 11px;
    line-height: 18px;
    padding: 1px 10px;
    margin-top: -5px;
  }

  ::v-deep .general-tabs {
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .app-loader {
    position: fixed;
    z-index: 1;
    top: 0;
  }
</style>
